import api from "./axios";

export interface AiProvider {
    name: string;
    enabled: boolean;
    has_key: boolean;
    model: string;
    base_url: string | null;
    requires_key: boolean;
    failover_order: number | null;
}

export interface AiFailover {
    order: string[];
    source: "settings" | "env" | null;
}

export interface AiConversation {
    id: string;
    title: string;
    created_at: string;
    updated_at: string;
}

export interface AiConversationMessage {
    role: "user" | "assistant";
    content: string;
    created_at: string;
}

export interface AiProviderTestResult {
    response: string;
    response_time: number;
    model: string;
}

export interface AiChatResult {
    text: string;
    conversation_id: string;
}

export interface AiGradeSuggestion {
    suggested_points: number;
    feedback: string;
    reasoning: string;
    confidence: string;
}

export interface AiQuestionDraftOption {
    option_text: string;
    is_correct: boolean;
}

export interface AiQuestionDraft {
    question: string;
    type: string;
    options: AiQuestionDraftOption[];
    points: number;
    difficulty: string;
    explanation: string | null;
    tags: string[];
}

export interface AiDashboardInsight {
    summary: string;
    highlights: string[];
    risks: string[];
    recommendations: string[];
}

export interface AiTaskSnapshot<T = unknown> {
    task_id: string;
    status: "queued" | "running" | "completed" | "failed";
    result: T | null;
    error: string | null;
}

export interface AiTaskDispatched {
    task_id: string;
    status: string;
}

const API_PREFIX = import.meta.env.VITE_API_BASE_URL || "/api";

class AiService {
    getProviders(): Promise<Record<string, AiProvider>> {
        return api
            .get("/v1/ai-providers")
            .then((response) => response.data.data.providers);
    }

    updateProvider(
        key: string,
        payload: {
            api_key?: string;
            model?: string;
            base_url?: string;
            enabled?: boolean;
        },
    ): Promise<AiProvider> {
        return api
            .put(`/v1/ai-providers/${key}`, payload)
            .then((response) => response.data.data);
    }

    testProvider(key: string): Promise<AiProviderTestResult> {
        return api
            .post(`/v1/ai-providers/${key}/test`)
            .then((response) => response.data.data);
    }

    getFailover(): Promise<AiFailover> {
        return api
            .get("/v1/ai-providers/failover")
            .then((response) => response.data.data);
    }

    updateFailover(order: string[]): Promise<AiFailover> {
        return api
            .put("/v1/ai-providers/failover", { order })
            .then((response) => response.data.data);
    }

    getConversations(): Promise<AiConversation[]> {
        return api
            .get("/v1/assistant/conversations")
            .then((response) => response.data.data.conversations);
    }

    getMessages(conversationId: string): Promise<AiConversationMessage[]> {
        return api
            .get(`/v1/assistant/conversations/${conversationId}/messages`)
            .then((response) => response.data.data.messages);
    }

    deleteConversation(conversationId: string): Promise<void> {
        return api
            .delete(`/v1/assistant/conversations/${conversationId}`)
            .then(() => undefined);
    }

    chatStreamUrl(): string {
        return `${API_PREFIX}/v1/assistant/chat/stream`;
    }

    suggestEssayGrade(answerId: string): Promise<AiTaskDispatched> {
        return api
            .post(`/v1/exam-grading/essay/${answerId}/ai-suggest`)
            .then((response) => response.data.data);
    }

    suggestAssignmentGrade(submissionId: string): Promise<AiTaskDispatched> {
        return api
            .post(
                `/v1/assignment-grading/submissions/${submissionId}/ai-suggest`,
            )
            .then((response) => response.data.data);
    }

    generateQuestionDrafts(payload: {
        subject_id: string;
        topic: string;
        count?: number;
        types?: string[];
        points?: number;
        difficulty?: string;
        grade_level?: string;
    }): Promise<AiTaskDispatched> {
        return api
            .post("/v1/question-banks/ai-generate", payload)
            .then((response) => response.data.data);
    }

    suggestReportComments(reportCardId: string): Promise<AiTaskDispatched> {
        return api
            .post(`/v1/report-cards/${reportCardId}/ai-comment`)
            .then((response) => response.data.data);
    }

    getDashboardInsight(): Promise<AiTaskDispatched> {
        return api
            .post("/v1/ai-insights/dashboard")
            .then((response) => response.data.data);
    }

    getAiTask<T = unknown>(taskId: string): Promise<AiTaskSnapshot<T>> {
        return api
            .get(`/v1/ai-tasks/${taskId}`)
            .then((response) => response.data.data);
    }

    /**
     * The XSRF token for the fetch-based SSE call (axios attaches it
     * automatically, fetch does not).
     */
    xsrfToken(): string | null {
        const match = document.cookie
            .split("; ")
            .find((cookie) => cookie.startsWith("XSRF-TOKEN="));

        return match
            ? decodeURIComponent(match.slice("XSRF-TOKEN=".length))
            : null;
    }
}

export default new AiService();
